import type { Team } from '@foosball/shared'

/**
 * Determines which team benefited (scored positive points) from a goal
 * This is used for color-coding goals in the UI
 * 
 * The logic:
 * 1. First, determine which team the points are awarded TO (teamAffected)
 * 2. Then, check if those points are positive or negative
 * 3. If positive for that team → that team benefits
 * 4. If negative for that team → opposite team benefits
 */
export function getTeamThatScoredPoints(
  scoredByTeam: Team,
  goalType: string,
  touchedByDefender: boolean | undefined,
  pointsAwarded: number
): Team {
  // First, determine which team the points are awarded TO
  // This matches the backend PointsCalculator logic
  let teamAffected: Team = scoredByTeam
  
  // For own goals (except own_goal_attack), points go to the OPPOSITE team
  if (goalType.startsWith('own_goal_') && goalType !== 'own_goal_attack') {
    // For own goals from defense/midfield/goalkeeper (with instant loss), 
    // if touched, points go to opposite team
    if (touchedByDefender) {
      teamAffected = scoredByTeam === 'red' ? 'blue' : 'red'
    }
    // If not touched, it's instant loss (handled separately, but points would be 0)
  }
  
  // For own_goal_attack, points go to the team that scored (yes, really!)
  // This is already handled by teamAffected = scoredByTeam above
  
  // For gamelle, points go to the OPPOSITE team (they receive negative points)
  if (goalType === 'gamelle' || goalType.startsWith('gamelle_')) {
    teamAffected = scoredByTeam === 'red' ? 'blue' : 'red'
  }
  
  // For all other goals (normal shots, monsterblock), points go to scoredByTeam
  // This is already handled by teamAffected = scoredByTeam above
  
  // Now determine who benefits based on the sign of the points
  if (pointsAwarded < 0) {
    // Negative points for teamAffected means opposite team benefits
    return teamAffected === 'red' ? 'blue' : 'red'
  }
  
  // Positive points (or zero) for teamAffected means that team benefits
  return teamAffected
}

/**
 * Gets a readable label for the event type
 */
export function getEventLabel(
  goalType: string,
  scoredByTeam: Team,
  touchedByDefender?: boolean
): { label: string; icon: string } {
  if (goalType.startsWith('own_goal_')) {
    const position = goalType.replace('own_goal_', '')
    return { label: `Own Goal (${position})`, icon: '❌' }
  }
  
  if (goalType.startsWith('monsterblock_')) {
    const position = goalType.replace('monsterblock_', '')
    return { label: `Monsterblock (${position})`, icon: '🛡️' }
  }
  
  if (goalType === 'gamelle') {
    return { label: 'Gamelle', icon: '🎯' }
  }
  
  // Regular shots
  const position = goalType.replace('_shot', '')
  const touched = touchedByDefender ? ' (touched)' : ''
  
  const positionLabels: Record<string, string> = {
    attack: 'Attack',
    midfield: 'Midfield',
    defense: 'Defense',
    goalkeeper: 'Goalkeeper'
  }
  
  return { 
    label: `${positionLabels[position] || position}${touched}`,
    icon: position === 'goalkeeper' ? '🚀' : position === 'defense' ? '💪' : '⚽'
  }
}

